import React, { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Smart Tournament Import Component
 * Uses AI (GPT-4o-mini) to intelligently parse Excel/CSV tournament files
 */
const ImportTorneoAI = ({ token, clubId, onSuccess, onCancel, showMsg }) => {
  // State
  const [step, setStep] = useState('upload'); // upload, analyzing, preview, creating, success
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [tournamentName, setTournamentName] = useState('');
  const [tournamentType, setTournamentType] = useState('torneo');
  const [playerMapping, setPlayerMapping] = useState({});
  const [error, setError] = useState(null);
  
  // Toast helper - uses showMsg prop if provided, otherwise console
  const notify = useCallback((type, message) => {
    if (showMsg) {
      showMsg(type, message);
    } else {
      console.log(`[${type}] ${message}`);
    }
  }, [showMsg]);

  // API helper
  const api = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    return response;
  }, [token]);

  // Handle file drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(ext)) {
      setError('Formato non supportato. Usa CSV o Excel (.xlsx, .xls)');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    setStep('analyzing');
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api('/api/clubs/import-torneo/analyze', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisResult(data);
        setTournamentName(data.tournament_name || selectedFile.name.replace(/\.[^/.]+$/, ''));
        setStep('preview');
        notify('success', 'File analizzato con successo!');
      } else {
        setError(data.error || 'Errore durante l\'analisi');
        setStep('upload');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Errore durante l\'analisi del file');
      setStep('upload');
    }
  };

  // Create tournament from imported data
  const handleCreateTournament = async () => {
    if (!tournamentName.trim()) {
      notify('error', 'Inserisci un nome per il torneo');
      return;
    }
    
    setStep('creating');
    
    try {
      const response = await api('/api/clubs/import-torneo/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tournament_name: tournamentName,
          tournament_type: tournamentType,
          matches: analysisResult.matches,
          player_mapping: playerMapping
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStep('success');
        notify('success', data.message);
        if (onSuccess) {
          setTimeout(() => onSuccess(data.tournament_id), 1500);
        }
      } else {
        setError(data.detail || 'Errore durante la creazione');
        setStep('preview');
      }
    } catch (err) {
      console.error('Create error:', err);
      setError('Errore durante la creazione del torneo');
      setStep('preview');
    }
  };

  // Reset everything
  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setAnalysisResult(null);
    setTournamentName('');
    setTournamentType('torneo');
    setPlayerMapping({});
    setError(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">🤖</span> Import Torneo Smart
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Importa tornei da Excel/CSV con intelligenza artificiale
          </p>
        </div>
        {onCancel && step !== 'success' && (
          <button 
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            ✕
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <div className="flex items-center gap-2">
            <span className="text-red-500">❌</span>
            <p className="text-red-700">{error}</p>
          </div>
          {analysisResult?.suggestions && (
            <ul className="mt-2 text-sm text-red-600 space-y-1">
              {analysisResult.suggestions.map((s, i) => (
                <li key={i}>💡 {s}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Step: Upload */}
      {step === 'upload' && (
        <div
          className={`
            border-2 border-dashed rounded-xl p-12 text-center transition-all
            ${dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className="hidden"
            id="ai-import-file"
          />
          <label htmlFor="ai-import-file" className="cursor-pointer">
            <div className="text-6xl mb-4">📁</div>
            <p className="text-lg font-medium text-gray-700 mb-2">
              Trascina qui il file o clicca per selezionare
            </p>
            <p className="text-sm text-gray-500">
              Formati supportati: CSV, Excel (.xlsx, .xls)
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm">
              <span>🤖</span> Analisi automatica con AI
            </div>
          </label>
        </div>
      )}

      {/* Step: Analyzing */}
      {step === 'analyzing' && (
        <div className="text-center py-12">
          <div className="relative inline-block">
            <div className="animate-spin h-16 w-16 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl">🤖</span>
            </div>
          </div>
          <p className="text-gray-600 mt-4 text-lg">Analisi AI in corso...</p>
          <p className="text-gray-400 text-sm mt-2">
            GPT-4o-mini sta analizzando il file "{file?.name}"
          </p>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && analysisResult && (
        <div className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {analysisResult.statistics?.total_matches || 0}
              </div>
              <div className="text-sm text-blue-700">Partite</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {analysisResult.statistics?.unique_players || 0}
              </div>
              <div className="text-sm text-green-700">Giocatori</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {analysisResult.statistics?.total_goals || 0}
              </div>
              <div className="text-sm text-purple-700">Gol Totali</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">
                {analysisResult.statistics?.avg_goals_per_match || 0}
              </div>
              <div className="text-sm text-orange-700">Media Gol</div>
            </div>
          </div>

          {/* Tournament Config */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-gray-800">⚙️ Configurazione Torneo</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Torneo *
              </label>
              <input
                type="text"
                value={tournamentName}
                onChange={(e) => setTournamentName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Es: Campionato Primavera 2024"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Torneo
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tournamentType"
                    value="torneo"
                    checked={tournamentType === 'torneo'}
                    onChange={(e) => setTournamentType(e.target.value)}
                    className="text-blue-600"
                  />
                  <span>🏆 Torneo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tournamentType"
                    value="campionato"
                    checked={tournamentType === 'campionato'}
                    onChange={(e) => setTournamentType(e.target.value)}
                    className="text-blue-600"
                  />
                  <span>📊 Campionato</span>
                </label>
              </div>
            </div>
          </div>

          {/* Players List */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3">👥 Giocatori Rilevati</h3>
            <div className="flex flex-wrap gap-2">
              {analysisResult.statistics?.players_list?.map((player, i) => {
                const matchInfo = analysisResult.player_matching?.[player];
                return (
                  <span 
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${
                      matchInfo?.matched 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {matchInfo?.matched && '✓ '}
                    {player}
                  </span>
                );
              })}
            </div>
            {analysisResult.existing_members_count > 0 && (
              <p className="text-sm text-gray-500 mt-2">
                ✓ = Corrisponde a un socio esistente nel club
              </p>
            )}
          </div>

          {/* Matches Preview */}
          <div className="overflow-x-auto">
            <h3 className="font-bold text-gray-800 mb-3">📋 Anteprima Partite (prime 10)</h3>
            <table className="w-full text-sm border rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">Casa</th>
                  <th className="px-3 py-2 text-center">Risultato</th>
                  <th className="px-3 py-2 text-left">Trasferta</th>
                  <th className="px-3 py-2 text-left">Fase</th>
                </tr>
              </thead>
              <tbody>
                {analysisResult.matches?.slice(0, 10).map((match, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{match.player_home}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-bold">{match.score_home}</span>
                      <span className="mx-1 text-gray-400">-</span>
                      <span className="font-bold">{match.score_away}</span>
                    </td>
                    <td className="px-3 py-2 font-medium">{match.player_away}</td>
                    <td className="px-3 py-2 text-gray-500">{match.phase || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {analysisResult.matches?.length > 10 && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                ... e altre {analysisResult.matches.length - 10} partite
              </p>
            )}
          </div>

          {/* Warnings */}
          {analysisResult.warnings?.length > 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
              <h4 className="font-medium text-yellow-800 mb-2">⚠️ Avvisi</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                {analysisResult.warnings.map((w, i) => (
                  <li key={i}>• {w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleCreateTournament}
              disabled={!tournamentName.trim()}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              ✅ Crea Torneo ({analysisResult.matches?.length || 0} partite)
            </button>
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              ❌ Annulla
            </button>
          </div>
        </div>
      )}

      {/* Step: Creating */}
      {step === 'creating' && (
        <div className="text-center py-12">
          <div className="animate-spin h-16 w-16 border-4 border-green-200 border-t-green-600 rounded-full mx-auto"></div>
          <p className="text-gray-600 mt-4 text-lg">Creazione torneo in corso...</p>
          <p className="text-gray-400 text-sm mt-2">
            Salvataggio di {analysisResult?.matches?.length || 0} partite
          </p>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-bold text-green-600 mb-2">
            Torneo Importato!
          </h3>
          <p className="text-gray-600">
            "{tournamentName}" è stato creato con successo
          </p>
          <button
            onClick={handleReset}
            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            📥 Importa un altro torneo
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportTorneoAI;
